import React, {
  useCallback,
  ForwardRefRenderFunction,
  useImperativeHandle,
  useState,
  useMemo,
  forwardRef,
} from "react";

import { useTranslation } from "react-i18next";

import { Despatch } from "../../../../../models/despatch";
import { DespatchViewDetails } from "./components/Details";
import { DespatchViewProducts } from "./components/Products";
import { DespatchViewShippingItems } from "./components/ShippingItems";
import DespatchSideViewComponent from "./components/SideView";
import DespatchViewSkeleton from "./components/Skeleton";
import { DespatchViewTruck } from "./components/Truck";
import useAction from "./hooks/useAction";
import { DespatchViewNavigation, DespatchViewTab } from "./types";

interface Props {}

export interface DespatchViewandles {
  open: (dispatchId: number) => void;
  close: () => void;
}

const DespatchViewComponent: ForwardRefRenderFunction<DespatchViewandles, Props> = (_, ref) => {
  const { getDespatchView, getProductsByDespatchView } = useAction();
  const { t: translate } = useTranslation();

  const [keyId, setKeyId] = useState<number>();
  const { data: despatch = null, isFetching } = getDespatchView(Number(keyId));
  const { data: products = [], isFetching: isFetchingProducts } = getProductsByDespatchView(
    Number(keyId)
  );

  const [isSidebarOpened, setIsSidebarOpened] = useState<boolean>(false);

  const onOpenSidebar = useCallback(
    (dispatchId: number) => {
      // eslint-disable-next-line eqeqeq
      if (keyId != dispatchId) setKeyId(dispatchId);

      setIsSidebarOpened(true);
    },
    [keyId]
  );

  const onCloseSidebar = useCallback(() => {
    setIsSidebarOpened(false);
  }, []);

  const sidebarTitle = useMemo(() => {
    let title = translate("despatch");
    const reference = despatch?.id;

    if (reference) title = `${title} #${reference}`;

    return title;
  }, [despatch]);

  const tabs = useMemo(() => {
    const tabs: Array<DespatchViewTab> = [
      {
        key: DespatchViewNavigation.DETAILS,
        label: translate("details"),
        loading: isFetching || isFetchingProducts,
        isEmpty: !despatch,
        isOpened: false,
        render: () => <DespatchViewDetails despatch={despatch} />,
        renderLoading: () => <DespatchViewSkeleton />,
      },
      {
        key: DespatchViewNavigation.PRODUCTS,
        label: translate("products"),
        loading: isFetching || isFetchingProducts,
        isEmpty: !products?.length,
        isOpened: false,
        render: () => <DespatchViewProducts despatch={{ products } as Despatch} />,
        renderLoading: () => <DespatchViewSkeleton />,
      },
      {
        key: DespatchViewNavigation.SHIPPING_ITEMS,
        label: translate("shippingItems"),
        loading: isFetching || isFetchingProducts,
        isEmpty: !despatch?.shippingItems?.length,
        isOpened: false,
        render: () => <DespatchViewShippingItems despatch={despatch} />,
        renderLoading: () => <DespatchViewSkeleton />,
      },
      {
        key: DespatchViewNavigation.TRUCK,
        label: translate("truck"),
        loading: isFetching || isFetchingProducts,
        isEmpty: !despatch?.fleets?.length,
        isOpened: false,
        render: () => <DespatchViewTruck despatch={despatch} />,
        renderLoading: () => <DespatchViewSkeleton />,
      },
    ];

    return tabs;
  }, [despatch, isFetching, products, isFetchingProducts]);

  useImperativeHandle(ref, () => ({
    open: onOpenSidebar,
    close: onCloseSidebar,
  }));

  return (
    <DespatchSideViewComponent
      isLoading={isFetching || isFetchingProducts}
      isOpened={isSidebarOpened}
      onClose={onCloseSidebar}
      tabs={tabs}
      title={sidebarTitle}
      despatch={despatch}
      buttonsComponent={[]}
    />
  );
};

export default forwardRef(DespatchViewComponent);
